// Inspired from https://github.com/costidotdev/shadcn-number-input/blob/main/src/components/number-input.tsx
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import * as React from 'react';
import { NumericFormat, type NumericFormatProps } from 'react-number-format';

import { Button } from '../Button';
import { cn } from '../utils';

import { inputVariants } from './inputVariants';

export interface NumberInputProps
  extends Omit<
    NumericFormatProps,
    | 'value'
    | 'onChange'
    | 'onValueChange'
    | 'thousandSeparator'
    | 'decimalSeparator'
  > {
  stepper?: number;
  placeholder?: string;
  defaultValue?: number;
  min?: number;
  max?: number;
  value?: number | null; // Controlled value
  onChange?: (value: number | null) => void;
  suffix?: string;
  prefix?: string;
  fixedDecimalScale?: boolean;
  decimalScale?: number;
  variant?: 'default' | 'inline';
  showStepper?: boolean;
  wrapperClassName?: string;
  thousandSeparator?: string;
  decimalSeparator?: string;
}

const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      stepper,
      placeholder,
      defaultValue,
      min = Number.MIN_SAFE_INTEGER,
      max = Number.MAX_SAFE_INTEGER,
      onChange,
      onBlur,
      fixedDecimalScale = false,
      decimalScale = 0,
      suffix,
      prefix,
      value: controlledValue,
      variant = 'default',
      className,
      showStepper = true,
      wrapperClassName,
      ...props
    },
    ref,
  ) => {
    const thousandSeparator = props.thousandSeparator || ',';
    const decimalSeparator = props.decimalSeparator || '.';

    const [value, setValue] = useControllableState({
      prop: controlledValue,
      onChange: onChange,
      defaultProp: defaultValue ?? null,
    });

    const handleValueChange = useCallback(
      (values: { floatValue?: number }) => {
        const { floatValue } = values;
        setValue(floatValue ?? null);
        if (onChange) {
          onChange(floatValue ?? null);
        }
      },
      [onChange, setValue],
    );

    const handleBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
      onBlur?.(e);
      if (value != null) {
        if (value < min) {
          setValue(min);
        } else if (value > max) {
          setValue(max);
        }
      }
    };

    // Internal ref required, as accessing `current` from an external ref that's passed in
    // as a RefCallBack (from react-hook-form for exmaple) is not possible
    const internalRef = useRef<HTMLInputElement | null>(null);

    const handleIncrement = useCallback(() => {
      setValue(
        value == null ? (stepper ?? 1) : Math.min(value + (stepper ?? 1), max),
      );
    }, [stepper, max, value]);

    const handleDecrement = useCallback(() => {
      setValue(
        value == null ? -(stepper ?? 1) : Math.max(value - (stepper ?? 1), min),
      );
    }, [stepper, min, value]);

    const handleKeyDown = useCallback(
      (e: KeyboardEvent) => {
        if (document.activeElement === internalRef.current) {
          if (e.key === 'ArrowUp') {
            handleIncrement();
          } else if (e.key === 'ArrowDown') {
            handleDecrement();
          }
        }
      },
      [handleIncrement, handleDecrement],
    );

    useEffect(() => {
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }, [handleKeyDown]);

    // Sync the internal ref with the external ref
    useImperativeHandle(ref, () => internalRef.current as HTMLInputElement);

    return (
      <div
        className={cn(
          'flex items-center focus-within:ring-2 focus-within:ring-primary rounded-md',
          variant === 'inline' && 'inline-flex rounded-lg',
          wrapperClassName,
        )}
        onBlur={handleBlur}
      >
        <NumericFormat
          autoComplete="off"
          value={value ?? ''}
          onValueChange={(val, source) => {
            if (source.source === 'prop') return;
            handleValueChange(val);
          }}
          decimalScale={decimalScale}
          fixedDecimalScale={fixedDecimalScale}
          allowNegative={min < 0}
          valueIsNumericString
          max={max}
          min={min}
          suffix={suffix}
          prefix={prefix}
          placeholder={placeholder}
          className={cn(
            inputVariants({ variant, className }),
            '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none relative',
            'focus-visible:ring-0',
            showStepper && 'rounded-r-none',
          )}
          getInputRef={internalRef}
          {...props}
          thousandSeparator={thousandSeparator}
          decimalSeparator={decimalSeparator}
          isAllowed={({ floatValue }) => {
            // Only block values that are clearly impossible, otherwise user is blocked during typing
            // min / max handled in onBlur function
            if (floatValue && min >= 0 && floatValue < 0) return false;
            return true;
          }}
        />
        {showStepper && (
          <div className={cn('flex flex-col')}>
            <Button
              aria-label="Increase value"
              className={cn(
                'px-2 h-6 border-l-0 rounded-none rounded-tr-md border-b-[0.5px] focus-visible:relative',
                variant === 'inline' && 'h-4 px-1 rounded-tr-lg',
              )}
              variant="outlined"
              color="secondary"
              onClick={handleIncrement}
              disabled={props.disabled}
            >
              <ChevronUp size={15} />
            </Button>
            <Button
              aria-label="Decrease value"
              className={cn(
                'px-2 h-6 border-l-0 rounded-none rounded-br-md border-t-[0.5px] focus-visible:relative disabled:cursor-not-allowed',
                variant === 'inline' && 'h-4 px-1 rounded-br-lg',
              )}
              variant="outlined"
              color="secondary"
              onClick={handleDecrement}
              disabled={props.disabled}
            >
              <ChevronDown size={15} />
            </Button>
          </div>
        )}
      </div>
    );
  },
);
NumberInput.displayName = 'NumberInput';

export { NumberInput };
